import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type {
    EnquiryErrors,
    EnquiryField,
    EnquiryInput,
    EnquiryRole
} from '@/lib/enquiry'

import { useEffect, useRef, useState } from 'react'

import {
    Building2,
    ChevronDown,
    CircleAlert,
    CircleCheck,
    GraduationCap,
    LoaderCircle,
    MessageCircle,
    Send
} from 'lucide-react'
import { cn } from '@/lib/utils'

import {
    COURSE_OPTIONS,
    EDUCATION_OPTIONS,
    ENQUIRY_ROLE_EVENT,
    LIMITS,
    OPENINGS_OPTIONS,
    emptyEnquiry,
    validateEnquiry
} from '@/lib/enquiry'

import { LINKS } from '@/constants/landing'

type Status = 'idle' | 'submitting' | 'success' | 'error'

type ApiResponse = {
    ok?: boolean
    message?: string
    errors?: EnquiryErrors
}

interface FieldProps {
    field: EnquiryField
    label: string
    required?: boolean
    optional?: boolean
    error?: string
    hint?: string
    className?: string
    children: ReactNode
}

const ROLE_OPTIONS: Array<{ value: EnquiryRole, label: string, icon: LucideIcon }> = [
    { value: 'learner', label: "I want to learn", icon: GraduationCap },
    { value: 'recruiter', label: "I want to hire", icon: Building2 }
]

const FIELD_ORDER: EnquiryField[] = [
    'fullName',
    'phone',
    'email',
    'city',
    'course',
    'education',
    'company',
    'openings',
    'rolesNeeded',
    'message'
]

const fieldId = (field: EnquiryField): string => `enquiry-${field}`

const controlClass = (hasError: boolean): string => cn(
    'block w-full rounded-xl border bg-background px-4 text-[15px] text-foreground shadow-xs outline-none transition-[border-color,box-shadow] placeholder:text-muted-foreground/70 focus:border-primary focus:ring-4 focus:ring-primary/15',
    hasError ? 'border-destructive focus:border-destructive focus:ring-destructive/15' : 'border-input'
)

const Field: FC<FieldProps> = ({
    field,
    label,
    required = false,
    optional = false,
    error,
    hint,
    className,
    children
}): ReactNode => {
    const id = fieldId(field)

    return (
        <div className={className}>
            <label htmlFor={id} className='flex items-center justify-between gap-3 text-sm font-semibold text-foreground'>
                <span>
                    {label}
                    {required && <span aria-hidden='true' className='text-destructive'> *</span>}
                </span>

                {optional && <span className='text-xs font-medium text-muted-foreground'>Optional</span>}
            </label>

            <div className='mt-2'>
                {children}
            </div>

            {error ? (
                <p id={`${id}-error`} className='mt-2 flex items-start gap-1.5 text-sm text-destructive'>
                    <CircleAlert className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                    {error}
                </p>
            ) : hint ? (
                <p id={`${id}-hint`} className='mt-2 text-xs text-muted-foreground'>
                    {hint}
                </p>
            ) : null}
        </div>
    )
}

const describedBy = (field: EnquiryField, errors: EnquiryErrors, hasHint = false): string | undefined => {
    if (errors[field]) return `${fieldId(field)}-error`
    if (hasHint) return `${fieldId(field)}-hint`

    return undefined
}

const EnquiryForm: FC = (): ReactNode => {
    const [values, setValues] = useState<EnquiryInput>(() => emptyEnquiry('learner'))
    const [errors, setErrors] = useState<EnquiryErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [serverMessage, setServerMessage] = useState('')
    const [submitted, setSubmitted] = useState<{ name: string, role: EnquiryRole } | null>(null)
    const formRef = useRef<HTMLFormElement>(null)
    const honeypotRef = useRef<HTMLInputElement>(null)

    // Calls to action elsewhere on the page can preselect the role
    useEffect(() => {
        const onRoleSelected = (event: Event): void => {
            const role = (event as CustomEvent<EnquiryRole>).detail

            if (role !== 'learner' && role !== 'recruiter') return

            setValues(current => ({ ...current, role }))
            setErrors({})
            setServerMessage('')
            setStatus(current => (current === 'submitting' ? current : 'idle'))
        }

        window.addEventListener(ENQUIRY_ROLE_EVENT, onRoleSelected)

        return () => window.removeEventListener(ENQUIRY_ROLE_EVENT, onRoleSelected)
    }, [])

    const isRecruiter = values.role === 'recruiter'

    const update = (field: EnquiryField) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>): void => {
        const { value } = event.target

        setValues(current => ({ ...current, [field]: value }))

        if (errors[field]) {
            setErrors(current => {
                const next = { ...current }

                delete next[field]

                return next
            })
        }
    }

    const chooseRole = (role: EnquiryRole): void => {
        setValues(current => ({ ...current, role }))
        setErrors({})
        setServerMessage('')
    }

    const focusFirstError = (found: EnquiryErrors): void => {
        const first = FIELD_ORDER.find(field => found[field])

        if (!first) return

        formRef.current?.querySelector<HTMLElement>(`#${fieldId(first)}`)?.focus()
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (status === 'submitting') return

        const { data, errors: found } = validateEnquiry(values)

        if (!data) {
            setErrors(found)
            setStatus('idle')
            focusFirstError(found)

            return
        }

        setStatus('submitting')
        setServerMessage('')

        try {
            const response = await fetch('/api/enquiries', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...values, website: honeypotRef.current?.value ?? '' })
            })

            const payload = await response.json().catch(() => null) as ApiResponse | null

            if (response.ok && payload?.ok) {
                setSubmitted({ name: data.fullName.split(' ')[0] || data.fullName, role: data.role })
                setValues(emptyEnquiry(data.role))
                setErrors({})
                setStatus('success')

                return
            }

            if (payload?.errors) {
                setErrors(payload.errors)
                focusFirstError(payload.errors)
            }

            setServerMessage(payload?.message || "Something went wrong. Please try again, or reach us on WhatsApp.")
            setStatus('error')
        }

        catch {
            setServerMessage("We couldn't reach our team just now. Check your connection and try again, or reach us on WhatsApp.")
            setStatus('error')
        }
    }

    if (status === 'success' && submitted) {
        return (
            <div role='status' className='flex flex-col items-center py-8 text-center sm:py-12'>
                <span className='grid size-16 place-items-center rounded-full bg-accent text-brand-teal-ink'>
                    <CircleCheck className='size-8' aria-hidden='true' />
                </span>

                <h3 className='mt-6 text-2xl font-semibold text-foreground'>
                    Thank you, {submitted.name}.
                </h3>

                <p className='mt-3 max-w-md leading-relaxed text-muted-foreground'>
                    {submitted.role === 'recruiter'
                        ? "We have your hiring requirement. Our team will call you to understand the roles and share suitable candidates."
                        : "We have your request. Our team will call you back to help you pick the right course."}
                </p>

                <div className='mt-8 flex flex-col gap-3 sm:flex-row'>
                    <a
                        href={submitted.role === 'recruiter' ? LINKS.whatsappRecruiter : LINKS.whatsappLearner}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='inline-flex h-12 items-center justify-center gap-2 rounded-full bg-primary px-6 font-semibold text-primary-foreground transition-colors hover:bg-brand-navy'
                    >
                        <MessageCircle className='size-5' aria-hidden='true' />
                        Chat on WhatsApp
                        <span className='sr-only'>(opens in a new tab)</span>
                    </a>

                    <button
                        type='button'
                        onClick={() => {
                            setStatus('idle')
                            setSubmitted(null)
                        }}
                        className='inline-flex h-12 items-center justify-center rounded-full border border-border bg-card px-6 font-semibold text-foreground transition-colors hover:border-primary/40 hover:text-primary'
                    >
                        Send another request
                    </button>
                </div>
            </div>
        )
    }

    return (
        <form ref={formRef} noValidate onSubmit={handleSubmit} aria-labelledby='enquiry-title' className='relative'>
            <h3 id='enquiry-title' className='text-2xl font-semibold text-foreground'>
                Request a callback
            </h3>

            <p className='mt-2 text-muted-foreground'>
                Share a few details and our team will call you back.
            </p>

            <fieldset className='mt-7'>
                <legend className='text-sm font-semibold text-foreground'>
                    What brings you here?
                </legend>

                <div className='mt-3 grid grid-cols-2 gap-1.5 rounded-2xl bg-muted p-1.5'>
                    {ROLE_OPTIONS.map(option => {
                        const Icon = option.icon
                        const selected = values.role === option.value

                        return (
                            <label
                                key={option.value}
                                className={cn(
                                    'flex cursor-pointer items-center justify-center gap-2 rounded-xl px-3 py-3 text-sm font-semibold transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary sm:text-[15px]',
                                    selected ? 'bg-white text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'
                                )}
                            >
                                <input
                                    type='radio'
                                    name='role'
                                    value={option.value}
                                    checked={selected}
                                    onChange={() => chooseRole(option.value)}
                                    className='sr-only'
                                />
                                <Icon className='size-4 shrink-0' aria-hidden='true' />
                                {option.label}
                            </label>
                        )
                    })}
                </div>
            </fieldset>

            <div className='mt-6 grid gap-5 sm:grid-cols-2'>
                <Field field='fullName' label="Full name" required error={errors.fullName}>
                    <input
                        id={fieldId('fullName')}
                        name='fullName'
                        type='text'
                        autoComplete='name'
                        required
                        maxLength={LIMITS.fullName}
                        value={values.fullName}
                        onChange={update('fullName')}
                        aria-invalid={Boolean(errors.fullName)}
                        aria-describedby={describedBy('fullName', errors)}
                        className={cn(controlClass(Boolean(errors.fullName)), 'h-12')}
                    />
                </Field>

                <Field field='phone' label="Phone number" required error={errors.phone} hint="We will call or WhatsApp you on this number.">
                    <input
                        id={fieldId('phone')}
                        name='phone'
                        type='tel'
                        inputMode='tel'
                        autoComplete='tel'
                        required
                        maxLength={20}
                        placeholder='98765 43210'
                        value={values.phone}
                        onChange={update('phone')}
                        aria-invalid={Boolean(errors.phone)}
                        aria-describedby={describedBy('phone', errors, true)}
                        className={cn(controlClass(Boolean(errors.phone)), 'h-12')}
                    />
                </Field>

                <Field
                    field='email'
                    label={isRecruiter ? "Work email" : "Email"}
                    required={isRecruiter}
                    optional={!isRecruiter}
                    error={errors.email}
                >
                    <input
                        id={fieldId('email')}
                        name='email'
                        type='email'
                        autoComplete='email'
                        required={isRecruiter}
                        maxLength={LIMITS.email}
                        value={values.email}
                        onChange={update('email')}
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={describedBy('email', errors)}
                        className={cn(controlClass(Boolean(errors.email)), 'h-12')}
                    />
                </Field>

                <Field field='city' label={isRecruiter ? "Hiring city" : "Your city"} required error={errors.city}>
                    <input
                        id={fieldId('city')}
                        name='city'
                        type='text'
                        autoComplete={isRecruiter ? 'off' : 'address-level2'}
                        required
                        maxLength={LIMITS.city}
                        value={values.city}
                        onChange={update('city')}
                        aria-invalid={Boolean(errors.city)}
                        aria-describedby={describedBy('city', errors)}
                        className={cn(controlClass(Boolean(errors.city)), 'h-12')}
                    />
                </Field>

                {isRecruiter ? (
                    <>
                        <Field field='company' label="Company name" required error={errors.company}>
                            <input
                                id={fieldId('company')}
                                name='company'
                                type='text'
                                autoComplete='organization'
                                required
                                maxLength={LIMITS.company}
                                value={values.company}
                                onChange={update('company')}
                                aria-invalid={Boolean(errors.company)}
                                aria-describedby={describedBy('company', errors)}
                                className={cn(controlClass(Boolean(errors.company)), 'h-12')}
                            />
                        </Field>

                        <Field field='openings' label="Number of openings" required error={errors.openings}>
                            <div className='relative'>
                                <select
                                    id={fieldId('openings')}
                                    name='openings'
                                    required
                                    value={values.openings}
                                    onChange={update('openings')}
                                    aria-invalid={Boolean(errors.openings)}
                                    aria-describedby={describedBy('openings', errors)}
                                    className={cn(controlClass(Boolean(errors.openings)), 'h-12 appearance-none pr-10', !values.openings && 'text-muted-foreground')}
                                >
                                    <option value='' disabled>Choose a range</option>
                                    {OPENINGS_OPTIONS.map(option => (
                                        <option key={option} value={option} className='text-foreground'>
                                            {option}
                                        </option>
                                    ))}
                                </select>

                                <ChevronDown className='pointer-events-none absolute right-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' aria-hidden='true' />
                            </div>
                        </Field>

                        <Field field='rolesNeeded' label="Roles you want to fill" optional error={errors.rolesNeeded} className='sm:col-span-2'>
                            <input
                                id={fieldId('rolesNeeded')}
                                name='rolesNeeded'
                                type='text'
                                autoComplete='off'
                                maxLength={LIMITS.rolesNeeded}
                                placeholder="For example, tele sales, field sales, customer support"
                                value={values.rolesNeeded}
                                onChange={update('rolesNeeded')}
                                aria-invalid={Boolean(errors.rolesNeeded)}
                                aria-describedby={describedBy('rolesNeeded', errors)}
                                className={cn(controlClass(Boolean(errors.rolesNeeded)), 'h-12')}
                            />
                        </Field>
                    </>
                ) : (
                    <>
                        <Field field='course' label="Course you are interested in" required error={errors.course}>
                            <div className='relative'>
                                <select
                                    id={fieldId('course')}
                                    name='course'
                                    required
                                    value={values.course}
                                    onChange={update('course')}
                                    aria-invalid={Boolean(errors.course)}
                                    aria-describedby={describedBy('course', errors)}
                                    className={cn(controlClass(Boolean(errors.course)), 'h-12 appearance-none pr-10', !values.course && 'text-muted-foreground')}
                                >
                                    <option value='' disabled>Choose a course</option>
                                    {COURSE_OPTIONS.map(option => (
                                        <option key={option} value={option} className='text-foreground'>
                                            {option}
                                        </option>
                                    ))}
                                </select>

                                <ChevronDown className='pointer-events-none absolute right-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' aria-hidden='true' />
                            </div>
                        </Field>

                        <Field field='education' label="Education" optional error={errors.education}>
                            <div className='relative'>
                                <select
                                    id={fieldId('education')}
                                    name='education'
                                    value={values.education}
                                    onChange={update('education')}
                                    aria-invalid={Boolean(errors.education)}
                                    aria-describedby={describedBy('education', errors)}
                                    className={cn(controlClass(Boolean(errors.education)), 'h-12 appearance-none pr-10', !values.education && 'text-muted-foreground')}
                                >
                                    <option value=''>Select your education</option>
                                    {EDUCATION_OPTIONS.map(option => (
                                        <option key={option} value={option} className='text-foreground'>
                                            {option}
                                        </option>
                                    ))}
                                </select>

                                <ChevronDown className='pointer-events-none absolute right-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' aria-hidden='true' />
                            </div>
                        </Field>
                    </>
                )}

                <Field field='message' label="Message" optional error={errors.message} className='sm:col-span-2'>
                    <textarea
                        id={fieldId('message')}
                        name='message'
                        rows={4}
                        maxLength={LIMITS.message}
                        placeholder={isRecruiter ? "Anything else we should know about the roles?" : "Tell us about your goals or any questions you have."}
                        value={values.message}
                        onChange={update('message')}
                        aria-invalid={Boolean(errors.message)}
                        aria-describedby={describedBy('message', errors)}
                        className={cn(controlClass(Boolean(errors.message)), 'min-h-28 resize-y py-3')}
                    />
                </Field>
            </div>

            <div aria-hidden='true' className='absolute -left-[10000px] top-auto h-px w-px overflow-hidden'>
                <label htmlFor='enquiry-website'>Leave this field empty</label>
                <input ref={honeypotRef} id='enquiry-website' name='website' type='text' tabIndex={-1} autoComplete='off' defaultValue='' />
            </div>

            {status === 'error' && serverMessage && (
                <div role='alert' className='mt-6 flex items-start gap-3 rounded-2xl border border-destructive/20 bg-destructive/5 p-4 text-sm text-destructive'>
                    <CircleAlert className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                    <p>{serverMessage}</p>
                </div>
            )}

            <div className='mt-8 flex flex-col-reverse gap-4 sm:flex-row sm:items-center sm:justify-between'>
                <p className='text-xs leading-relaxed text-muted-foreground sm:max-w-xs'>
                    By sending this, you agree that Skillkafe may contact you about your request by phone, WhatsApp or email.
                </p>

                <button
                    type='submit'
                    disabled={status === 'submitting'}
                    className='inline-flex h-13 shrink-0 items-center justify-center gap-2 rounded-full bg-primary px-8 font-semibold text-primary-foreground shadow-[0_14px_30px_-14px_rgba(43,58,143,0.8)] transition-colors hover:bg-brand-navy disabled:cursor-not-allowed disabled:opacity-70'
                >
                    {status === 'submitting' ? (
                        <>
                            <LoaderCircle className='size-5 animate-spin' aria-hidden='true' />
                            Sending
                        </>
                    ) : (
                        <>
                            Request a callback
                            <Send className='size-4' aria-hidden='true' />
                        </>
                    )}
                </button>
            </div>
        </form>
    )
}

export default EnquiryForm
